import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from '@react-google-maps/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { MapPin, Locate, Search } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Troia, Portugal coordinates
const TROIA_CENTER = {
  lat: 38.4925,
  lng: -8.8975
};

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '16px'
};

const libraries = ['places'];

const LocationPicker = ({ 
  value, 
  onChange, 
  onCoordinatesChange,
  language = 'pt',
  placeholder 
}) => {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState(TROIA_CENTER);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    onCoordinatesChange?.(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);

    // Fallback address written IMMEDIATELY so the user always sees feedback,
    // even if Google Geocoding/Places API is restricted or rate-limited.
    const fallbackAddr = `${language === 'pt' ? 'Localização no mapa' : 'Map location'} (${lat.toFixed(5)}, ${lng.toFixed(5)})`;
    onChange?.(fallbackAddr);

    // Try to upgrade to a human-readable address asynchronously.
    const writeIfBetter = (addr) => {
      if (addr && addr.trim().length > 0) onChange?.(addr);
    };

    // POI click → use Places API to get the place name + formatted address
    if (e.placeId && window.google?.maps?.places && mapRef.current) {
      try { e.stop(); } catch (_) { /* ignore */ }
      try {
        const svc = new window.google.maps.places.PlacesService(mapRef.current);
        svc.getDetails(
          { placeId: e.placeId, fields: ['formatted_address', 'name'] },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
              const parts = [place.name, place.formatted_address].filter(Boolean);
              const seen = new Set();
              const dedup = parts.filter((p) => (seen.has(p) ? false : seen.add(p)));
              writeIfBetter(dedup.join(' — '));
            }
          }
        );
      } catch (_) { /* ignore */ }
    }

    // Reverse geocode (works for both POI and plain map clicks)
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          writeIfBetter(results[0].formatted_address);
        }
      });
    } catch (_) { /* ignore */ }
  }, [onChange, onCoordinatesChange, language]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onAutocompleteLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });
        onChange?.(place.formatted_address || place.name);
        onCoordinatesChange?.(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        
        if (mapRef.current) {
          mapRef.current.panTo({ lat, lng });
          mapRef.current.setZoom(17);
        }
      }
    }
  }, [onChange, onCoordinatesChange]);

  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMarkerPosition({ lat, lng });
          setMapCenter({ lat, lng });
          onCoordinatesChange?.(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          // Immediate fallback so the field is never empty
          onChange?.(`${language === 'pt' ? 'Minha localização' : 'My location'} (${lat.toFixed(5)}, ${lng.toFixed(5)})`);

          if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(17);
          }

          // Try to upgrade to human-readable address
          try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === 'OK' && results && results[0]) {
                onChange?.(results[0].formatted_address);
              }
            });
          } catch (_) { /* ignore */ }
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  }, [onChange, onCoordinatesChange, language]);

  if (loadError) {
    return (
      <div className="p-4 bg-red-50 rounded-xl text-red-600 text-sm">
        {language === 'pt' ? 'Erro ao carregar o mapa' : 'Error loading map'}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-[300px] bg-gray-100 rounded-xl animate-pulse flex items-center justify-center">
        <MapPin className="text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Input with Autocomplete */}
      <div className="relative">
        <Autocomplete
          onLoad={onAutocompleteLoad}
          onPlaceChanged={onPlaceChanged}
          restrictions={{ country: 'pt' }}
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9FA2B4]" size={18} />
            <Input
              type="text"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder || (language === 'pt' ? 'Pesquisar localização...' : 'Search location...')}
              className="pl-10 pr-12 rounded-xl"
              data-testid="location-search-input"
            />
          </div>
        </Autocomplete>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 -translate-y-1/2"
          onClick={getCurrentLocation}
          data-testid="get-current-location"
        >
          <Locate size={18} className="text-[#FF6B35]" />
        </Button>
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={14}
        onClick={onMapClick}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
        {markerPosition && (
          <Marker
            position={markerPosition}
            animation={window.google.maps.Animation.DROP}
            icon={{
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#FF6B35" stroke="white" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3" fill="white"></circle>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 40)
            }}
          />
        )}
      </GoogleMap>

      {/* Instructions */}
      <p className="text-xs text-[#9FA2B4]">
        {language === 'pt' 
          ? '💡 Clique no mapa para marcar a localização exata de entrega ou use a pesquisa.'
          : '💡 Click on the map to mark the exact delivery location or use search.'}
      </p>
    </div>
  );
};

export default LocationPicker;
